import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Slide,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  IosShare as IosShareIcon,
  AddBox as AddBoxIcon
} from '@mui/icons-material';
import useInstallPrompt from '../hooks/useInstallPrompt';

const InstallPrompt = () => {
  const {
    isInstallable,
    isInstalled,
    isPWA,
    isIOS,
    isAndroid,
    promptInstall,
    dismissPrompt
  } = useInstallPrompt();

  // Detect browser for custom messaging
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isFirefox = /Firefox/.test(userAgent);
  const isSamsungInternet = /SamsungBrowser/.test(userAgent);
  const isOpera = /OPR/.test(userAgent) || /Opera/.test(userAgent);
  const isUCBrowser = /UCBrowser/.test(userAgent);
  const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
  const isEdge = /Edg/.test(userAgent);
  
  // Desktop detection - only based on user agent, not screen size
  const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isDesktop = !isMobileDevice;

  const [showPrompt, setShowPrompt] = useState(false);
  const [shownThisSession, setShownThisSession] = useState(false);
  const [splashCompleted, setSplashCompleted] = useState(false);

  // Listen for splash completion
  useEffect(() => {
    const handleSplashCompleted = () => {
      setSplashCompleted(true);
    };

    window.addEventListener('splashCompleted', handleSplashCompleted);
    return () => window.removeEventListener('splashCompleted', handleSplashCompleted);
  }, []);

  useEffect(() => {
    console.log('InstallPrompt useEffect', { isInstalled, isPWA, shownThisSession, isInstallable, splashCompleted, isDesktop });
    
    // Don't show if:
    // - Already installed as PWA
    // - Already shown this session
    // - On desktop (not mobile)
    if (isInstalled || isPWA || shownThisSession || isDesktop) {
      console.log('Not showing prompt - already installed, shown, or desktop');
      return;
    }

    // Only show on mobile (iOS or Android)
    if (!isIOS && !isAndroid) {
      console.log('Not showing prompt - not a mobile device');
      return;
    }

    if (!isInstallable) {
      console.log('Not showing prompt - not installable');
      return;
    }

    // If splash completed, show prompt immediately
    if (splashCompleted) {
      console.log('Showing prompt immediately after splash');
      setShowPrompt(true);
      setShownThisSession(true);
      sessionStorage.setItem('installPromptShown', 'true');
      return;
    }

    // Check if splash screen was shown (by checking if localStorage has appOpenedBefore)
    const wasFirstTime = localStorage.getItem('appOpenedBefore');
    
    // If splash was shown (first time), wait for splash to complete
    const delay = wasFirstTime ? 3000 : 15000; // 15 seconds if splash likely showed
    
    console.log('Will show prompt in', delay/1000, 'seconds');
    
    // Show prompt after delay
    const timer = setTimeout(() => {
      console.log('Showing prompt after delay');
      setShowPrompt(true);
      setShownThisSession(true);
      sessionStorage.setItem('installPromptShown', 'true');
    }, delay);

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled, isPWA, shownThisSession, splashCompleted, isDesktop, isIOS, isAndroid]);

  const handleInstall = async () => {
    console.log('Install button clicked', { isIOS, isAndroid, isInstallable });
    
    if (isIOS) {
      // iOS - Just highlight the instructions, can't programmatically install
      return;
    }

    // Android/Desktop - Trigger install
    console.log('Calling promptInstall...');
    const accepted = await promptInstall();
    console.log('promptInstall result:', accepted);
    
    if (accepted) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
    setShowPrompt(false);
  };

  // Add test button for debugging (only in development)
  if (process.env.NODE_ENV === 'development' && !showPrompt) {
    return (
      <Box sx={{ position: 'fixed', top: 10, right: 10, zIndex: 9999 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            console.log('Force showing prompt for testing');
            setShowPrompt(true);
          }}
          sx={{ backgroundColor: 'red' }}
        >
          Test Install Prompt
        </Button>
      </Box>
    );
  }

  // Don't render if shouldn't show (including desktop)
  if (!showPrompt || isInstalled || isPWA || isDesktop) {
    return null;
  }

  return (
    <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: 70, sm: 20 }, // Above bottom nav on mobile
          left: { xs: 16, sm: '50%' },
          right: { xs: 16, sm: 'auto' },
          transform: { xs: 'none', sm: 'translateX(-50%)' },
          maxWidth: { xs: '100%', sm: 500 },
          zIndex: 1300,
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)',
          color: 'white',
          boxShadow: '0 8px 32px rgba(10, 42, 102, 0.4)',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleDismiss}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          aria-label="Close install prompt"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ p: 3, pt: 2 }}>
          {/* Header */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              <img
                src="/logo3.png"
                alt="NISTYLEZ"
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                Install NISTYLEZ
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Get the full app experience
              </Typography>
            </Box>
          </Stack>

          {/* Benefits */}
          <Stack spacing={1} sx={{ mb: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                }}
              />
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Faster loading and smoother experience
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                }}
              />
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Quick access from your home screen
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                }}
              />
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Works offline and uses less data
              </Typography>
            </Stack>
          </Stack>

          {/* Universal Installation Instructions */}
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
              Follow these instructions to add the app:
            </Typography>
            <Stack spacing={1}>
              {isIOS ? (
                // iOS Instructions
                <>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="1"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Tap the <IosShareIcon sx={{ fontSize: 16, mx: 0.5, verticalAlign: 'middle' }} /> Share button at the bottom
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="2"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Scroll down and tap "Add to Home Screen"
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="3"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Tap "Add" to install the app
                    </Typography>
                  </Stack>
                </>
              ) : isAndroid ? (
                // Android Instructions
                <>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="1"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Tap the menu button (⋮) in your browser
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="2"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Look for "Add to Home Screen" or "Install App"
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="3"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Tap to install the app
                    </Typography>
                  </Stack>
                </>
              ) : (
                // Desktop Instructions
                <>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="1"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Look for the install icon in your browser's address bar
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="2"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Click the install icon or look in the browser menu
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      label="3"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: '600',
                        width: 24,
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Follow the prompts to install the app
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>

          {/* Action Button */}
          <Button
            variant="outlined"
            fullWidth
            onClick={handleDismiss}
            startIcon={<AddBoxIcon />}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.5)',
              color: 'white',
              fontWeight: '600',
              py: 1.5,
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Follow Instructions to Add App
          </Button>
        </Box>
      </Paper>
    </Slide>
  );
};

export default InstallPrompt;

